/**
 * Utilitários de validação e formatação para formulários brasileiros
 */

/**
 * Valida um CNPJ brasileiro
 * @param {string} cnpj - CNPJ com ou sem formatação
 * @returns {boolean} true se válido
 */
export const validateCNPJ = (cnpj) => {
  // Remove caracteres não numéricos
  const cleanCNPJ = cnpj.replace(/\D/g, '');
  
  // Verifica se tem 14 dígitos
  if (cleanCNPJ.length !== 14) return false;
  
  // Verifica se todos os dígitos são iguais (inválido)
  if (/^(\d)\1+$/.test(cleanCNPJ)) return false;
  
  // Validação do primeiro dígito verificador
  let sum = 0;
  let weight = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleanCNPJ[i]) * weight[i];
  }
  let remainder = sum % 11;
  let digit1 = remainder < 2 ? 0 : 11 - remainder;
  
  if (parseInt(cleanCNPJ[12]) !== digit1) return false;
  
  // Validação do segundo dígito verificador
  sum = 0;
  weight = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleanCNPJ[i]) * weight[i];
  }
  remainder = sum % 11;
  let digit2 = remainder < 2 ? 0 : 11 - remainder;
  
  if (parseInt(cleanCNPJ[13]) !== digit2) return false;
  
  return true;
};

/**
 * Valida um CPF brasileiro
 * @param {string} cpf - CPF com ou sem formatação
 * @returns {boolean} true se válido
 */
export const validateCPF = (cpf) => {
  const cleanCPF = cpf.replace(/\D/g, '');
  
  if (cleanCPF.length !== 11) return false;
  if (/^(\d)\1+$/.test(cleanCPF)) return false;
  
  // Validação primeiro dígito
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCPF[i]) * (10 - i);
  }
  let digit1 = (sum * 10) % 11;
  if (digit1 === 10) digit1 = 0;
  if (parseInt(cleanCPF[9]) !== digit1) return false;
  
  // Validação segundo dígito
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCPF[i]) * (11 - i);
  }
  let digit2 = (sum * 10) % 11;
  if (digit2 === 10) digit2 = 0;
  if (parseInt(cleanCPF[10]) !== digit2) return false;
  
  return true;
};

/**
 * Valida CPF ou CNPJ
 * @param {string} value - CPF ou CNPJ com ou sem formatação
 * @returns {boolean} true se válido
 */
export const validateCPFCNPJ = (value) => {
  const clean = value.replace(/\D/g, '');
  if (clean.length === 11) return validateCPF(value);
  if (clean.length === 14) return validateCNPJ(value);
  return false;
};

/**
 * Formata um valor numérico para o padrão brasileiro de moeda
 * @param {number|string} value - Valor numérico
 * @returns {string} Valor formatado (ex: "1.234,56")
 */
export const formatCurrencyBR = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  
  const num = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;
  if (isNaN(num)) return '';
  
  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/**
 * Converte uma string no formato brasileiro para número
 * @param {string} value - Valor no formato brasileiro (ex: "1.234,56")
 * @returns {number} Valor numérico
 */
export const parseCurrencyBR = (value) => {
  if (!value) return 0;
  // Remove pontos de milhar e substitui vírgula por ponto
  const cleanValue = value.toString().replace(/\./g, '').replace(',', '.');
  return parseFloat(cleanValue) || 0;
};

/**
 * Handler para input de moeda no padrão brasileiro
 * @param {string} value - Valor digitado
 * @returns {string} Valor formatado
 */
export const handleCurrencyInput = (value) => {
  // Remove tudo que não é número
  let numbers = value.replace(/\D/g, '');
  
  // Converte para centavos
  let cents = parseInt(numbers) || 0;
  
  // Converte para reais
  let reais = cents / 100;
  
  // Formata
  return reais.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/**
 * Formata telefone no padrão brasileiro
 * @param {string} value - Telefone
 * @returns {string} Telefone formatado
 */
export const formatPhoneBR = (value) => {
  const numbers = value.replace(/\D/g, '');
  
  if (numbers.length <= 2) {
    return numbers;
  } else if (numbers.length <= 6) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  } else if (numbers.length <= 10) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
  } else {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  }
};

/**
 * Gera um slug a partir de um texto
 * @param {string} text - Texto para converter
 * @returns {string} Slug gerado
 */
export const generateSlug = (text) => {
  if (!text) return '';
  
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[^a-z0-9\s-]/g, '') // Remove caracteres especiais
    .replace(/\s+/g, '-') // Substitui espaços por hífens
    .replace(/-+/g, '-') // Remove hífens duplicados
    .replace(/^-|-$/g, ''); // Remove hífens do início e fim
};
